import React, { useRef, useState, useCallback, useEffect } from "react";

const SCROLL_ZONE = 60;
const SCROLL_SPEED = 12;

const ResizableGridContainer = ({
  children,
  defaultHeight = 700,
  minHeight = 300,
  maxHeight = 1400,
  className = "ag-theme-alpine",
  style = {},
}) => {
  const [height, setHeight] = useState(defaultHeight);
  const containerRef = useRef(null);
  const dragging = useRef(false);
  const startY = useRef(0);
  const startH = useRef(0);
  const scrollRaf = useRef(null);

  const autoScroll = useCallback((clientY) => {
    const viewportH = window.innerHeight;
    if (clientY > viewportH - SCROLL_ZONE) {
      const intensity = (clientY - (viewportH - SCROLL_ZONE)) / SCROLL_ZONE;
      window.scrollBy(0, SCROLL_SPEED * Math.min(intensity, 1));
    } else if (clientY < SCROLL_ZONE) {
      const intensity = (SCROLL_ZONE - clientY) / SCROLL_ZONE;
      window.scrollBy(0, -SCROLL_SPEED * Math.min(intensity, 1));
    }
  }, []);

  const onMouseMove = useCallback(
    (e) => {
      if (!dragging.current) return;
      e.preventDefault();
      const delta = e.clientY - startY.current + (window.scrollY - dragging.current.scrollY);
      const next = Math.min(maxHeight, Math.max(minHeight, startH.current + delta));
      setHeight(next);
      autoScroll(e.clientY);
    },
    [minHeight, maxHeight, autoScroll]
  );

  const onMouseUp = useCallback(() => {
    dragging.current = false;
    cancelAnimationFrame(scrollRaf.current);
    document.removeEventListener("mousemove", onMouseMove);
    document.removeEventListener("mouseup", onMouseUp);
    document.body.style.userSelect = "";
    document.body.style.cursor = "";
  }, [onMouseMove]);

  const onMouseDown = useCallback(
    (e) => {
      e.preventDefault();
      dragging.current = { scrollY: window.scrollY };
      startY.current = e.clientY;
      startH.current = height;
      document.body.style.userSelect = "none";
      document.body.style.cursor = "ns-resize";
      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    },
    [height, onMouseMove, onMouseUp]
  );

  useEffect(() => {
    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      cancelAnimationFrame(scrollRaf.current);
    };
  }, [onMouseMove, onMouseUp]);

  return (
    <div style={{ position: "relative" }}>
      <div
        ref={containerRef}
        className={className}
        style={{ height, width: "100%", overflow: "hidden", ...style }}
      >
        {children}
      </div>
      <div
        onMouseDown={onMouseDown}
        style={{
          height: 8,
          cursor: "ns-resize",
          background: "linear-gradient(to bottom, #e0e0e0, #d0d0d0)",
          borderRadius: "0 0 4px 4px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          userSelect: "none",
        }}
      >
        <div
          style={{
            width: 40,
            height: 3,
            borderRadius: 2,
            background: "#aaa",
          }}
        />
      </div>
    </div>
  );
};

export default ResizableGridContainer;
